import {
  SPACE,
  KEYBOARD_KEYS,
  DECIMAL_SEPARATOR,
  DECIMAL_PART_SIZE,
  EMPTY_INTEGER_PART,
  THOUSAND_GROUP_SIZE,
  MAX_INTEGER_NUMBER_LENGTH,
} from "./constants";
import { AmountInputProps, HandleInputValueChangeParams } from "./types";

/**
 * Функция для определения является ли переданное значение числом
 *
 * Пример: isValidNumericValue(15) -> true
 * Пример: isValidNumericValue("123") -> false
 */
export const isValidNumericValue = (value: AmountInputProps["value"]) => {
  const numericValue = Number(value);

  if (
    (typeof value === "number" && !Number.isNaN(numericValue)) ||
    typeof value === "undefined" ||
    value === null
  ) {
    return true;
  }

  return false;
};

/**
 * Функция для разделения целой и десятичной частей значения суммы
 *
 * Пример: splitAmountValue("15,27") -> [15, 27]
 */
export const splitAmountValue = (value: string): string[] =>
  value.split(DECIMAL_SEPARATOR);

/**
 * Функция форматирования десятичной части суммы
 *
 * Пример: formatDecimalPart("1") -> "10"
 */
export const formatDecimalPart = (
  decimalPart = "",
  decimalPartSize = DECIMAL_PART_SIZE
): string => decimalPart.slice(0, decimalPartSize);

/**
 * Функция для отделения числовых разрядов пробелами
 *
 * Пример: formatIntegerPart("1234567") -> "1 234 567"
 */
export const formatIntegerPart = (integerPart: string): string => {
  if (!integerPart) {
    return "";
  }

  const thousandSeparator = SPACE;
  const thousandGroupPattern = new RegExp(
    `\\B(?=(\\d{${THOUSAND_GROUP_SIZE}})+(?!\\d))`,
    "g"
  );

  return integerPart
    .slice(0, MAX_INTEGER_NUMBER_LENGTH)
    .replace(thousandGroupPattern, thousandSeparator);
};

/**
 * Функция форматирования значения суммы
 *
 * Пример: formatAmountValue(1234567.89) -> "1 234 567,89"
 */
export const formatAmountValue = (
  amount: AmountInputProps["value"]
): string => {
  if (amount == null) {
    return "";
  }

  const [integerPart, decimalPart] = amount.toString().split(".");

  const formattedIntegerPart = formatIntegerPart(integerPart);

  if (!decimalPart) {
    return formattedIntegerPart;
  }

  const formattedDecimalPart = formatDecimalPart(decimalPart);

  return `${formattedIntegerPart},${formattedDecimalPart}`;
};

/**
 * Функция замены недопустимых знаков в числе строкового представления
 * @example: sanitizeValue("123 456,78") -> "123456.78"
 */
const sanitizeValue = (value: string): string =>
  value.replace(/\s/g, "").replace(DECIMAL_SEPARATOR, KEYBOARD_KEYS.DOT);

/**
 *
 * Функция приведения числа в строковом виде к числу
 *
 * Пример: clearValue("123 456,78") -> 123456.78
 */
export const clearValue = (value: string): number | null =>
  value.length ? Number.parseFloat(sanitizeValue(value)) : null;

/**
 * Функция обработки и форматирования значения после события onChange
 */
export const handleInputValueChange = ({
  value,
  lastKeyStroke,
  lastInputState,
  selectionStart,
}: HandleInputValueChangeParams): {
  value: string;
  cursorPosition: number;
} => {
  let cursorPosition = selectionStart;
  const splittedValue = value.replace(/\./g, DECIMAL_SEPARATOR).split("");
  const removeLastInsertedSymbol = (): void => {
    cursorPosition = cursorPosition > 0 ? cursorPosition - 1 : 0;
    splittedValue.splice(cursorPosition, 1);
  };

  // удаление всех знаков из поля ввода
  if (splittedValue.length === 0) {
    return { cursorPosition: 0, value: "" };
  }

  if (!/^[\d\s,\.]*$/.test(value)) {
    const diffLength = splittedValue.length - lastInputState.value.length;

    return {
      cursorPosition: cursorPosition - diffLength,
      value: lastInputState.value,
    };
  }

  // запрещаем удаление пробела, вместо этого смещаем курсор на одну позицию соответственно
  if (
    lastKeyStroke === KEYBOARD_KEYS.DELETE &&
    lastInputState.value[cursorPosition - 1] === KEYBOARD_KEYS.SPACE &&
    splittedValue[cursorPosition - 1] !== KEYBOARD_KEYS.SPACE
  ) {
    splittedValue.splice(cursorPosition - 1, 0, KEYBOARD_KEYS.SPACE);
    cursorPosition += 1;
  } else if (
    lastKeyStroke === KEYBOARD_KEYS.BACKSPACE &&
    lastInputState.value[cursorPosition] === KEYBOARD_KEYS.SPACE
  ) {
    splittedValue.splice(cursorPosition, 0, KEYBOARD_KEYS.SPACE);
  }

  if (
    String(clearValue(splittedValue.join(""))).split(KEYBOARD_KEYS.DOT)[0]
      .length > MAX_INTEGER_NUMBER_LENGTH
  ) {
    const diffLength = lastInputState.value.length - splittedValue.length;

    return {
      cursorPosition: cursorPosition - diffLength,
      value: lastInputState.value,
    };
  }

  if (splittedValue.includes(DECIMAL_SEPARATOR)) {
    // переводим каретку за разделитель при вводе разделителя
    if (
      splittedValue.filter((symbol) => symbol === DECIMAL_SEPARATOR).length >
        1 &&
      (lastKeyStroke === DECIMAL_SEPARATOR ||
        lastKeyStroke === KEYBOARD_KEYS.DOT)
    ) {
      if (splittedValue[cursorPosition] === DECIMAL_SEPARATOR) {
        splittedValue.splice(cursorPosition - 1, 1);
      } else {
        removeLastInsertedSymbol();
      }

      return { value: splittedValue.join(""), cursorPosition };
    }

    if (
      splittedValue.filter((symbol) => symbol === DECIMAL_SEPARATOR).length > 1
    ) {
      return {
        cursorPosition: lastInputState.caretPosition,
        value: lastInputState.value,
      };
    }

    const decimalSeparatorIndex = splittedValue.indexOf(DECIMAL_SEPARATOR);

    // 12, 34 -> 12,34
    for (let i = decimalSeparatorIndex; i < splittedValue.length; i++) {
      const symbol = splittedValue[i];

      if (symbol !== KEYBOARD_KEYS.SPACE) {
        continue;
      }

      splittedValue.splice(i, 1);
      cursorPosition = cursorPosition > i ? cursorPosition - 1 : cursorPosition;
    }

    // 12,3456 -> 12,34
    splittedValue.splice(
      decimalSeparatorIndex + DECIMAL_SEPARATOR.length + DECIMAL_PART_SIZE,
      Infinity
    );

    // ,12 -> 0,12
    if (splittedValue[0] === DECIMAL_SEPARATOR) {
      splittedValue.unshift(KEYBOARD_KEYS[0]);
      cursorPosition += 1;
    }
  } else {
  }

  // форматируем число по разрядам со сдвигом курсора
  const integerPart = splittedValue.includes(DECIMAL_SEPARATOR)
    ? splittedValue.slice(0, splittedValue.indexOf(DECIMAL_SEPARATOR))
    : splittedValue;

  // Удаляем все пробелы и смещаем каретку соответственно
  const { spaceCount, integerPartWithoutSpaces } = integerPart.reduce<{
    spaceCount: number;
    integerPartWithoutSpaces: string[];
  }>(
    (accum, symbol, i) => {
      if (symbol === KEYBOARD_KEYS.SPACE) {
        accum.spaceCount =
          cursorPosition > i ? accum.spaceCount + 1 : accum.spaceCount;
      } else {
        accum.integerPartWithoutSpaces.push(symbol);
      }

      return accum;
    },
    { spaceCount: 0, integerPartWithoutSpaces: [] }
  );

  cursorPosition -= spaceCount;

  // Убираем лишние нули в начале числа
  if (/^0+/.test(integerPartWithoutSpaces.join(""))) {
    let zeroCount = 1;

    while (integerPartWithoutSpaces[zeroCount] === KEYBOARD_KEYS[0]) {
      zeroCount += 1;
    }

    // 0000,12 -> 0,12
    if (
      integerPartWithoutSpaces[zeroCount] === DECIMAL_SEPARATOR &&
      zeroCount > 1
    ) {
      integerPartWithoutSpaces.splice(0, zeroCount, KEYBOARD_KEYS[0]);
      cursorPosition -= zeroCount;
    } else if (/[1-9]/.test(integerPartWithoutSpaces[zeroCount])) {
      // 01 -> 1
      integerPartWithoutSpaces.splice(0, zeroCount);
      cursorPosition -= zeroCount;
    } else if (zeroCount === integerPartWithoutSpaces.length) {
      integerPartWithoutSpaces.splice(0, zeroCount, EMPTY_INTEGER_PART);
    }
  }

  // разделяем целочисленную часть пробелами начиная с конца
  if (integerPartWithoutSpaces.length > 3) {
    for (let i = integerPartWithoutSpaces.length - 3; i > 0; i -= 3) {
      integerPartWithoutSpaces.splice(i, 0, KEYBOARD_KEYS.SPACE);
      cursorPosition =
        cursorPosition + 1 > i ? cursorPosition + 1 : cursorPosition;
    }
  }

  const integerPartLength = splittedValue.includes(DECIMAL_SEPARATOR)
    ? splittedValue.slice(0, splittedValue.indexOf(DECIMAL_SEPARATOR)).length
    : splittedValue.length;

  splittedValue.splice(0, integerPartLength, ...integerPartWithoutSpaces);

  // устанавливаем курсор перед пробелом при нажатии Backspace после пробела
  if (
    lastKeyStroke === KEYBOARD_KEYS.BACKSPACE &&
    cursorPosition === selectionStart + 1 &&
    splittedValue.join("") === lastInputState.value
  ) {
    cursorPosition -= 1;
  }

  if (!/\d{1,3}(\s\d{3})*(,\d{,2})?/.test(splittedValue.join(""))) {
    return {
      cursorPosition: lastInputState.caretPosition,
      value: lastInputState.value,
    };
  }

  const joinedValue = splittedValue.join("");

  return { value: joinedValue, cursorPosition };
};

export const validateLimits = ({
  value,
  minValue,
  maxValue,
}: {
  value: string;
  minValue: AmountInputProps["minValue"];
  maxValue: AmountInputProps["maxValue"];
}): string => {
  const numericValue = clearValue(value);

  if (numericValue === null) {
    return value;
  }

  if (minValue != null && numericValue < minValue) {
    return formatAmountValue(minValue);
  }

  if (maxValue != null && numericValue > maxValue) {
    return formatAmountValue(maxValue);
  }

  return value;
};
